export const targetConditionTypeIds = ['constraint/asset', 'constraint/assetGroup', 'constraint/zone'];

const isSchedule = (filter: any) => filter?.type === 'constraint/schedule';

const isTargetCondition = (filter: any) => targetConditionTypeIds.includes(filter?.type);

const isSingleNegatedTargetCondition = (condition: any) => (
  condition?.type === 'operator/or'
  && condition.operands?.length === 1
  && condition.operands[0]?.type === 'operator/not'
  && isTargetCondition(condition.operands[0].operand)
);

const isEditableCondition = (condition: any) => (
  isTargetCondition(condition)
  || (condition?.type === 'operator/not' && isTargetCondition(condition.operand))
  || isSingleNegatedTargetCondition(condition)
);

const isOrGroupOperand = (operand: any) => (
  isTargetCondition(operand)
  || (operand?.type === 'operator/not' && isTargetCondition(operand.operand))
);

// A real "any" group. A single does-not-match stays OR([NOT(target)]) and is not one of these.
const isOrConditionGroup = (filter: any) => (
  filter?.type === 'operator/or'
  && !isSingleNegatedTargetCondition(filter)
  && (filter.operands || []).length > 0
  && (filter.operands || []).every(isOrGroupOperand)
);

export const hasUnsupportedObservationTriggerFilter = (filter: any): boolean => {

  if (!filter || isSchedule(filter) || isEditableCondition(filter)) return false;
  if (isOrConditionGroup(filter)) return false;
  if (filter.type === 'operator/and') {

    const conditions = (filter.operands || []).filter((operand: any) => !isSchedule(operand));
    if (conditions.length === 1 && isOrConditionGroup(conditions[0])) return false;

    // Nested groups are not shown by the editor. Treat them as unsupported so a save keeps the original filter.
    return conditions.some((condition: any) => !isEditableCondition(condition));

  }
  return true;

};

const withMatch = (condition: any, match: string) => ({
  ...condition,
  match,
});

const normalizeMatchCondition = (condition: any) => {

  if (isSingleNegatedTargetCondition(condition)) return withMatch(condition.operands[0].operand, 'doesNotMatch');
  if (condition?.type === 'operator/not') return withMatch(condition.operand, 'doesNotMatch');
  return withMatch(condition, 'matches');

};

export const readObservationTriggerFilter = (filter: any) => {

  if (!filter) {

    return {
      filterConditionBlockType: 'operator/and',
      filterConditions: [],
      filterHasUnsupportedConditions: false,
      filterSchedules: { entries: [] },
    };

  }

  const operands = filter.type === 'operator/and' ? filter.operands || [] : [filter];
  const scheduleOperands = operands.filter(isSchedule);
  let conditionOperands = operands.filter((operand: any) => !isSchedule(operand));
  let filterConditionBlockType = 'operator/and';

  if (conditionOperands.length === 1 && isOrConditionGroup(conditionOperands[0])) {

    filterConditionBlockType = 'operator/or';
    conditionOperands = conditionOperands[0].operands || [];

  }

  const filterHasUnsupportedConditions = hasUnsupportedObservationTriggerFilter(filter);
  const conditionsAreEditable = conditionOperands.every(isEditableCondition);

  return {
    filterConditionBlockType,
    filterConditions: filterHasUnsupportedConditions || !conditionsAreEditable
      ? []
      : conditionOperands.map(normalizeMatchCondition),
    filterHasUnsupportedConditions,
    filterSchedules: {
      entries: scheduleOperands.flatMap((operand: any) => operand.schedule?.entries || []),
    },
  };

};

const cleanConditionForApi = (condition: any) => {

  if (!condition?.type) return null;
  const {
    match,
    ...payload
  } = condition;

  if (match === 'doesNotMatch') {

    return {
      type: 'operator/or',
      operands: [{
        type: 'operator/not',
        operand: payload,
      }],
    };

  }
  return payload;

};

const negateConditionForAndOperand = (condition: any) => {

  if (isSingleNegatedTargetCondition(condition)) return condition.operands[0].operand;
  return {
    type: 'operator/or',
    operands: [{
      type: 'operator/not',
      operand: condition,
    }],
  };

};

const toOrOperand = (condition: any) => (
  isSingleNegatedTargetCondition(condition) ? condition.operands[0] : condition
);

export const buildObservationTriggerFilter = (data: any) => {

  const conditions = (data.filterConditions || [])
    .map(cleanConditionForApi)
    .filter((condition: any) => condition?.type);
  const scheduleEntries = (data.filterSchedules?.entries || [])
    .filter((entry: any) => entry?.daysOfWeek?.length && entry?.timeStart && entry?.timeEnd);
  let conditionOperands = conditions;
  if (data.filterConditionBlockType === 'operator/not' && conditions.length) {

    conditionOperands = conditions.map(negateConditionForAndOperand);

  } else if (data.filterConditionBlockType === 'operator/or' && conditions.length) {

    conditionOperands = [{
      type: 'operator/or',
      operands: conditions.map(toOrOperand),
    }];

  }

  return {
    type: 'operator/and',
    operands: [
      ...conditionOperands,
      ...(scheduleEntries.length ? [{
        type: 'constraint/schedule',
        schedule: { entries: scheduleEntries },
      }] : []),
    ],
  };

};
